import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui';
import { CompanyOrgChart } from '@/features/companies/components/CompanyOrgChart';
import { cn } from '@/lib/cn';

/**
 * Each company has its own reporting lines, so charts are grouped by company.
 * A chart only loads once its company is opened.
 */
export function OrgChartPanel({
  companies,
}: {
  companies: Array<{ _id: string; name: string }>;
}): React.ReactElement {
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(companies[0] ? [companies[0]._id] : [])
  );
  if (companies.length === 1) return <CompanyOrgChart companyId={companies[0]._id} />;
  return (
    <div className="space-y-3">
      {companies.map((company) => {
        const isOpen = open.has(company._id);
        const panelId = `org-chart-${company._id}`;
        return (
          <Card key={company._id} contentClassName="p-0">
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() =>
                setOpen((current) => {
                  const next = new Set(current);
                  if (next.has(company._id)) next.delete(company._id);
                  else next.add(company._id);
                  return next;
                })
              }
              className="flex w-full items-center justify-between gap-3 rounded-[var(--radius-panel)] px-4 py-3 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary dark:text-gray-100 dark:hover:bg-gray-700/40"
            >
              {company.name}
              <ChevronDown
                className={cn('h-4 w-4 shrink-0 transition-transform', isOpen && 'rotate-180')}
                aria-hidden
              />
            </button>
            {isOpen && (
              <div id={panelId} className="border-t border-gray-200 dark:border-gray-700">
                <CompanyOrgChart companyId={company._id} embedded />
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
